import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  createWwwAdminJob,
  deleteWwwAdminJob,
  fetchWwwAdminJobs,
  updateWwwAdminJob,
  type CreateWwwAdminJobInput,
  type UpdateWwwAdminJobInput,
  type WwwAdminJob,
  type WwwModerationStatus,
} from '../../api/adminWwwJobs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';

const QUERY_KEY = ['admin-www-jobs'];
const formatTimestamp = (value?: string | null) => {
  if (!value) return 'Unknown';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return 'Unknown';
  return parsed.toLocaleString();
};

type JobFormState = {
  slug: string;
  title: string;
  location: string;
  jobType: string;
  pay: string;
  schedule: string;
  overview: string;
  duties: string;
  requirements: string;
  benefits: string;
  tags: string;
  isPublished: boolean;
  moderationStatus: WwwModerationStatus;
};

const DEFAULT_FORM: JobFormState = {
  slug: '',
  title: '',
  location: '',
  jobType: 'Full-time',
  pay: '',
  schedule: '',
  overview: '',
  duties: '',
  requirements: '',
  benefits: '',
  tags: '',
  isPublished: true,
  moderationStatus: 'approved',
};

const statusBadgeClasses: Record<WwwModerationStatus, string> = {
  approved: 'border-emerald-400/40 bg-emerald-500/20 text-emerald-200',
  draft: 'border-white/20 bg-white/10 text-gray-200',
  pending_review: 'border-amber-400/40 bg-amber-500/20 text-amber-100',
  rejected: 'border-red-400/40 bg-red-500/20 text-red-200',
};

function parseLines(value: string) {
  return value
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);
}

function parseTags(value: string) {
  return value
    .split(',')
    .map((tag) => tag.trim())
    .filter(Boolean);
}

function buildPayload(form: JobFormState): CreateWwwAdminJobInput {
  return {
    slug: form.slug.trim(),
    title: form.title.trim(),
    location: form.location.trim(),
    jobType: form.jobType.trim(),
    pay: form.pay.trim(),
    schedule: form.schedule.trim() || undefined,
    overview: form.overview.trim(),
    duties: parseLines(form.duties),
    requirements: parseLines(form.requirements),
    benefits: parseLines(form.benefits),
    tags: parseTags(form.tags),
    isPublished: form.isPublished,
    moderationStatus: form.moderationStatus,
  };
}

function mapJobToForm(job: WwwAdminJob): JobFormState {
  return {
    slug: job.slug,
    title: job.title,
    location: job.location,
    jobType: job.jobType,
    pay: job.pay,
    schedule: job.schedule ?? '',
    overview: job.overview,
    duties: (job.duties ?? []).join('\n'),
    requirements: (job.requirements ?? []).join('\n'),
    benefits: (job.benefits ?? []).join('\n'),
    tags: (job.tags ?? []).join(', '),
    isPublished: job.isPublished,
    moderationStatus: job.moderationStatus,
  };
}

export function WwwJobsAdmin() {
  const queryClient = useQueryClient();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<JobFormState>(DEFAULT_FORM);

  const jobsQuery = useQuery({
    queryKey: QUERY_KEY,
    queryFn: fetchWwwAdminJobs,
    staleTime: 1000 * 15,
  });

  const createMutation = useMutation({
    mutationFn: createWwwAdminJob,
    onSuccess: () => {
      toast.success('WWW job created.');
      setForm(DEFAULT_FORM);
      setEditingId(null);
      void queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Unable to create WWW job.');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateWwwAdminJobInput }) => updateWwwAdminJob(id, payload),
    onSuccess: () => {
      toast.success('WWW job updated.');
      setForm(DEFAULT_FORM);
      setEditingId(null);
      void queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Unable to update WWW job.');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteWwwAdminJob,
    onSuccess: () => {
      toast.success('WWW job deleted.');
      void queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Unable to delete WWW job.');
    },
  });

  const saving = createMutation.isPending || updateMutation.isPending;
  const sortedJobs = useMemo(
    () => [...(jobsQuery.data ?? [])].sort((a, b) => (a.updatedAt ?? '').localeCompare(b.updatedAt ?? '') * -1),
    [jobsQuery.data],
  );

  const onSubmit = () => {
    const payload = buildPayload(form);
    if (!payload.slug || !payload.title || !payload.overview || !payload.location || !payload.jobType || !payload.pay) {
      toast.error('Please fill in slug, title, location, type, pay, and overview.');
      return;
    }

    if (editingId) {
      updateMutation.mutate({ id: editingId, payload });
      return;
    }
    createMutation.mutate(payload);
  };

  return (
    <main className="min-h-screen bg-[#05060f] pb-24" aria-labelledby="www-jobs-admin-title">
      <div className="mx-auto max-w-6xl space-y-8 px-4 py-16 sm:px-6 lg:px-8">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-xs uppercase tracking-[0.35em] text-[#f7ef8a]">Admin · WWW</p>
            <h1 id="www-jobs-admin-title" className="mt-2 text-3xl font-semibold text-white">
              Website Jobs
            </h1>
            <p className="text-sm text-gray-400">
              Create, edit, publish, and remove jobs shown on the marketing website.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              className="border-white/20 text-white hover:bg-white/10"
              onClick={() => jobsQuery.refetch()}
              disabled={jobsQuery.isFetching}
            >
              {jobsQuery.isFetching ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Refresh
            </Button>
            <Button asChild variant="outline" className="border-white/20 text-white hover:bg-white/10">
              <Link to="/admin/www/blog">Manage Blog Posts</Link>
            </Button>
          </div>
        </div>

        <Card className="border-white/10 bg-white/[0.03]">
          <CardHeader>
            <CardTitle className="text-white">{editingId ? 'Edit WWW job' : 'Post new WWW job'}</CardTitle>
            <CardDescription className="text-gray-400">
              Keep `isPublished` enabled and moderation status set to `approved` for public visibility on `/www/jobs`.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label className="text-white" htmlFor="www-job-title">
                  Job title
                </Label>
                <Input
                  id="www-job-title"
                  value={form.title}
                  onChange={(event) => setForm((current) => ({ ...current, title: event.target.value }))}
                  className="border-white/20 bg-white/[0.06] text-white"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-white" htmlFor="www-job-slug">
                  Slug
                </Label>
                <Input
                  id="www-job-slug"
                  value={form.slug}
                  onChange={(event) => setForm((current) => ({ ...current, slug: event.target.value }))}
                  className="border-white/20 bg-white/[0.06] text-white"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-white" htmlFor="www-job-location">
                  Location
                </Label>
                <Input
                  id="www-job-location"
                  value={form.location}
                  onChange={(event) => setForm((current) => ({ ...current, location: event.target.value }))}
                  className="border-white/20 bg-white/[0.06] text-white"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-white" htmlFor="www-job-type">
                  Job type
                </Label>
                <Input
                  id="www-job-type"
                  value={form.jobType}
                  onChange={(event) => setForm((current) => ({ ...current, jobType: event.target.value }))}
                  className="border-white/20 bg-white/[0.06] text-white"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-white" htmlFor="www-job-pay">
                  Pay
                </Label>
                <Input
                  id="www-job-pay"
                  value={form.pay}
                  onChange={(event) => setForm((current) => ({ ...current, pay: event.target.value }))}
                  className="border-white/20 bg-white/[0.06] text-white"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-white" htmlFor="www-job-schedule">
                  Schedule (optional)
                </Label>
                <Input
                  id="www-job-schedule"
                  value={form.schedule}
                  onChange={(event) => setForm((current) => ({ ...current, schedule: event.target.value }))}
                  className="border-white/20 bg-white/[0.06] text-white"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-white" htmlFor="www-job-overview">
                Overview
              </Label>
              <Textarea
                id="www-job-overview"
                rows={4}
                value={form.overview}
                onChange={(event) => setForm((current) => ({ ...current, overview: event.target.value }))}
                className="border-white/20 bg-white/[0.06] text-white"
              />
            </div>

            <div className="grid gap-4 md:grid-cols-3">
              <div className="space-y-2">
                <Label className="text-white" htmlFor="www-job-duties">
                  Duties (one per line)
                </Label>
                <Textarea
                  id="www-job-duties"
                  rows={6}
                  value={form.duties}
                  onChange={(event) => setForm((current) => ({ ...current, duties: event.target.value }))}
                  className="border-white/20 bg-white/[0.06] text-white"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-white" htmlFor="www-job-reqs">
                  Requirements (one per line)
                </Label>
                <Textarea
                  id="www-job-reqs"
                  rows={6}
                  value={form.requirements}
                  onChange={(event) => setForm((current) => ({ ...current, requirements: event.target.value }))}
                  className="border-white/20 bg-white/[0.06] text-white"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-white" htmlFor="www-job-benefits">
                  Benefits (one per line)
                </Label>
                <Textarea
                  id="www-job-benefits"
                  rows={6}
                  value={form.benefits}
                  onChange={(event) => setForm((current) => ({ ...current, benefits: event.target.value }))}
                  className="border-white/20 bg-white/[0.06] text-white"
                />
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-3">
              <div className="space-y-2 md:col-span-2">
                <Label className="text-white" htmlFor="www-job-tags">
                  Tags (comma separated)
                </Label>
                <Input
                  id="www-job-tags"
                  value={form.tags}
                  onChange={(event) => setForm((current) => ({ ...current, tags: event.target.value }))}
                  className="border-white/20 bg-white/[0.06] text-white"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-white">Moderation status</Label>
                <Select
                  value={form.moderationStatus}
                  onValueChange={(value) =>
                    setForm((current) => ({ ...current, moderationStatus: value as WwwModerationStatus }))
                  }
                >
                  <SelectTrigger className="border-white/20 bg-white/[0.06] text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="role-surface-admin border-white/10 bg-role-admin-overlay text-white backdrop-blur-xl">
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="pending_review">Pending review</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <label className="flex items-center gap-3 text-sm text-gray-300">
              <input
                type="checkbox"
                checked={form.isPublished}
                onChange={(event) => setForm((current) => ({ ...current, isPublished: event.target.checked }))}
              />
              Publish this job on the marketing website
            </label>

            <div className="flex flex-wrap gap-3">
              <Button
                type="button"
                onClick={onSubmit}
                className="bg-gold-gradient text-black hover:brightness-110"
                disabled={saving}
              >
                {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-2 h-4 w-4" />}
                {editingId ? 'Save Changes' : 'Create Job'}
              </Button>
              {editingId && (
                <Button
                  type="button"
                  variant="outline"
                  className="border-white/20 text-white hover:bg-white/10"
                  onClick={() => {
                    setEditingId(null);
                    setForm(DEFAULT_FORM);
                  }}
                >
                  Cancel edit
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="border-white/10 bg-white/[0.03]">
          <CardHeader>
            <CardTitle className="text-white">Existing WWW jobs</CardTitle>
            <CardDescription className="text-gray-400">
              Jobs are public only when `isPublished=true` and moderation status is `approved`.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {jobsQuery.isLoading && (
              <div className="flex items-center gap-2 text-sm text-gray-400">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading jobs…
              </div>
            )}
            {jobsQuery.isError && (
              <p className="text-sm text-red-300">
                {jobsQuery.error instanceof Error ? jobsQuery.error.message : 'Unable to load WWW jobs.'}
              </p>
            )}
            {!jobsQuery.isLoading && !jobsQuery.isError && sortedJobs.length === 0 && (
              <p className="text-sm text-gray-400">No jobs yet.</p>
            )}
            {sortedJobs.map((job) => (
              <Card key={job.id} className="border-white/10 bg-white/[0.02]">
                <CardContent className="flex flex-col gap-4 p-5 md:flex-row md:items-start md:justify-between">
                  <div className="space-y-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="text-base font-semibold text-white">{job.title}</p>
                      <Badge className={statusBadgeClasses[job.moderationStatus]}>
                        {job.moderationStatus.replace('_', ' ')}
                      </Badge>
                      <Badge className={job.isPublished ? 'bg-emerald-500/20 text-emerald-200' : 'bg-white/10 text-gray-300'}>
                        {job.isPublished ? 'Published' : 'Hidden'}
                      </Badge>
                    </div>
                    <p className="text-sm text-gray-400">
                      /jobs/{job.slug} · {job.location} · {job.jobType}
                    </p>
                    <p className="text-sm text-gray-300">{job.overview}</p>
                    <p className="text-xs text-gray-500">Updated: {formatTimestamp(job.updatedAt ?? job.createdAt)}</p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      variant="outline"
                      className="border-white/20 text-white hover:bg-white/10"
                      onClick={() => {
                        setEditingId(job.id);
                        setForm(mapJobToForm(job));
                      }}
                    >
                      <Pencil className="mr-2 h-4 w-4" />
                      Edit
                    </Button>
                    <Button
                      variant="outline"
                      className="border-white/20 text-white hover:bg-white/10"
                      onClick={() =>
                        updateMutation.mutate({
                          id: job.id,
                          payload: { isPublished: !job.isPublished },
                        })
                      }
                      disabled={updateMutation.isPending}
                    >
                      {job.isPublished ? 'Unpublish' : 'Publish'}
                    </Button>
                    <Button
                      variant="ghost"
                      className="text-red-300 hover:text-red-200"
                      onClick={() => {
                        const confirmed = window.confirm(`Delete WWW job "${job.title}"? This cannot be undone.`);
                        if (confirmed) {
                          deleteMutation.mutate(job.id);
                        }
                      }}
                      disabled={deleteMutation.isPending}
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Delete
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </CardContent>
        </Card>
      </div>
    </main>
  );
}
